<template>
    <div>
        <b-row>
            <b-col cols="12">
                <n-button v-if="showReportProblemButton" v-b-modal.report-problem v-b-tooltip.hover title="Create"
                    type="success">
                    {{ prescriptionsSelected.length == 0 ? 'Create a Patient Case' : `Report a problem with
                    ${prescriptionsSelected.length} Script${(prescriptionsSelected.length > 1 ? 's' : '')}` }}
                </n-button>
                <n-button v-if="showReportProblemButton" v-b-modal="'rx-suppress'" v-b-tooltip.hover
                    title="Pause Reminders" :disabled="(prescriptionsSelected.length == 0)" type="success">
                    Pause Refill Reminders
                </n-button>
                <b-button v-if="showProccessQueueButton" @click="openBatch" variant="success"
                    :disabled="disableBatchRefill">
                    {{ batchModalTitle }}
                </b-button>
                <b-button @click="transferOut" v-if="showTransferButton" v-b-modal.rx-transfer-modal variant="warning"
                    :disabled="disableTransfer">
                    Transfer Out
                </b-button>
            </b-col>
            <b-col v-if="showVoidBox && !alwaysShowVoid" class="d-flex align-items-center">
                <b-form-input
                    v-if="showFilter"
                    v-model="filterText"
                    type="search"
                    size="sm"
                    placeholder="Filter Scripts"
                    class="mr-3"
                    style="width: 200px;"
                />
                <checkbox inline v-model="showVoidRxs" style="margin: 0; padding-top: 0;">Show Void & Expired ({{ voidAndExpiredCount }})</checkbox>
            </b-col>
        </b-row>

        <b-row v-if="showOnlineOrderConflictAlert" class="mt-3">
            <b-col>
                <b-alert variant="danger" :show="showOnlineOrderConflictAlert">
                    <i class="fas fa-exclamation-triangle"></i>
                    One of selected prescriptions must be processed from the online order queue first: Rx #{{
                        rxOnUnprocessedOrder }}
                </b-alert>
            </b-col>
        </b-row>

        <b-row>
            <b-col>
                <b-table ref="prescriptionTable" id="prescription-history-table" striped responsive="true" sort-icon-left
                    :items="paginatedPrescriptions" :fields="DetermineFieldsByMode(mode)" :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc" :tbody-tr-class="colorCodeTable" show-empty :empty-text="_tableEmptyText"
                    :busy="isLoading" :sort-compare="customSort" :no-local-sorting="currentPerPage > 0 || (manualSort?.length ?? 0) > 0"
                    v-model="renderedItems" @sort-changed="$emit('sort-changed', $event)" @row-clicked="selectRow">
                    <template #table-busy>
                        <div class="text-center my-2">
                            <b-spinner class="align-middle mr-2"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>
                    <template #cell(hasAddons)="data" v-if="showAddOns">
                        <p class="h5 mb-2" style="text-align: center">
                            <b-icon v-b-tooltip.hover.html="getAddOns(data.item.addOnsList)" v-if="data.item.hasAddons"
                                icon="patch-plus" />
                        </p>
                    </template>
                    <template #cell(nextProcessDate)="data">
                        <b v-if="moment().isBefore(data.value)">{{ data.value }}</b>
                        <div v-else>{{ data.value }}</div>
                    </template>
                    <template #cell(select)="data" v-if="showSelectCheckox">
                        <input v-if="!singleSelect" type="checkbox" :value="data.item"
                            :checked="prescriptionsSelected.some(p => p.rxID === data.item.rxID)"
                            @click.stop="handleCheckboxClick($event, data.item)" inline />
                        <input v-else type="radio" :value="data.item"
                            :checked="prescriptionsSelected.some(p => p.rxID === data.item.rxID)"
                            @click.stop="handleCheckboxClick($event, data.item)" inline />
                    </template>
                    <template #cell(patientName)="data">
                        <div>
                            <span v-if="data.value.length > 0">{{ data.value }}</span>
                            <span v-else>{{ data.item.patientLastName }}, {{ data.item.patientFirstName }}</span>
                            <template v-if="patientCountFn">
                                <b-badge v-if="patientCountFn(data.item.patientID) && patientCountFn(data.item.patientID).visible > 1"
                                         variant="info" class="ml-1">
                                    {{ patientCountFn(data.item.patientID).visible }} Rx
                                </b-badge>
                                <b-badge v-if="patientCountFn(data.item.patientID) && patientCountFn(data.item.patientID).hidden > 0"
                                         variant="warning" class="ml-1">
                                    &#9888; +{{ patientCountFn(data.item.patientID).hidden }} hidden
                                </b-badge>
                            </template>
                        </div>
                    </template>
                    <template #cell(patientPhone)="data">
                        <template v-if="data.item.patientPhone">
                            <span>{{ data.value }}</span>
                            <PhoneCopyButton :phone="data.item.patientPhone" />
                        </template>
                    </template>
                    <template #cell(patientCell)="data">
                        <template v-if="data.item.patientCell">
                            <span>{{ data.value }}</span>
                            <PhoneCopyButton :phone="data.item.patientCell" />
                        </template>
                    </template>
                    <template #cell(rxId)="data">
                        <component :is="rxIdLink ? 'RouterLink' : 'span'" :to="{
                            name: 'PrescriptionDetailsId'
                            , params: {
                                rxId: data.item.rxId,
                            }
                        }">
                            <span v-if="data.item.isRefillableStatus" v-b-tooltip.hover
                                :title="rxIdTooltip(data.item)">
                                {{ data.item.rxId }}
                            </span>
                            <b v-else v-b-tooltip.hover
                                :title="rxIdTooltip(data.item)"
                                style="cursor: pointer;">
                                {{ data.item.rxId }}
                            </b>
                        </component>
                        <i v-if="data.item.isAutoRefillEnrolled" class="fa fa-redo text-info ml-1"
                            v-b-tooltip.hover title="Auto-Refill Enrolled" style="font-size: 0.8em;" />
                        <b-badge v-if="data.item.isUrgent" variant="danger" class="ml-1">URGENT</b-badge>
                        <b-badge v-if="data.item.needsReview" variant="warning" class="ml-1"
                                 v-b-tooltip.hover title="Latest auto-refill was voided/reversed. Open the Rx to review and reprocess manually.">
                            Needs Review
                        </b-badge>
                        <b-badge v-if="data.item.needsRefrigeration" variant="info" class="ml-1"
                            v-b-tooltip.hover title="Refrigerated item. Package with cold-chain handling.">
                            <i class="fas fa-snowflake"></i> REFRIGERATED
                        </b-badge>
                    </template>
                    <template #cell(medSync)="data">
                        <b-badge v-if="data.item.isRxInMedSync" variant="success"
                                 v-b-tooltip.hover title="This prescription is synced (MedSync).">
                            MedSync
                        </b-badge>
                        <b-badge v-else-if="data.item.isPatientInMedSync" variant="secondary"
                                 v-b-tooltip.hover title="Patient is enrolled in MedSync, but this prescription is not synced.">
                            MedSync Patient
                        </b-badge>
                    </template>
                    <template #cell(qtyLeft)="data">
                        {{ Math.max(0, Number((data.item.totalAllowedToDispense - data.item.totalDispensed).toFixed(2))) }}
                    </template>
                    <template #cell(fillDate)="data">
                        <b v-if="isFuture(data.value)" v-b-tooltip.hover title="Queued for future date">
                            {{ data.value }}
                        </b>
                        <span v-else>
                            {{ data.value }}
                        </span>
                    </template>
                    <template #cell(patientPay)="data">
                        {{ data.value | currency }}
                    </template>
                    <template #cell(promisedDate)="data">
                        <PromisedTimeSummary v-if="data.item.promisedDate" :value="data.item.promisedDate"
                            :icon-only="!promisedDateInline" :size="promisedDateInline ? 'sm' : undefined" />
                    </template>
                    <template #cell(rxIdWithExpirationWarning)="data">
                        <component :is="rxIdLink ? 'RouterLink' : 'span'" :to="{
                            name: 'PrescriptionDetailsId'
                            , params: {
                                rxId: data.item.rxId,
                            }
                        }">
                            {{ data.item.rxId }}
                        </component>
                        <i v-if="data.item.isAutoRefillEnrolled" class="fa fa-redo text-info ml-1"
                            v-b-tooltip.hover title="Auto-Refill Enrolled" style="font-size: 0.8em;" />
                        <div v-if="data.item.isUrgent">
                            <b-badge variant="danger">
                                <i class="fa fa-exclamation-circle"></i> URGENT
                            </b-badge>
                        </div>
                        <div v-if="data.item.needsRefrigeration">
                            <b-badge variant="info" v-b-tooltip.hover
                                title="Refrigerated item. Package with cold-chain handling.">
                                <i class="fas fa-snowflake"></i> REFRIGERATED
                            </b-badge>
                        </div>
                        <div v-if="moment(data.item.expirationDate).diff(moment(), 'days') <= 7">
                            <b-badge variant="danger">
                                <i class="fa fa-warning"></i>
                                Expires
                                {{ data.item.expirationDate | formatDate('L', false) }}
                            </b-badge>
                        </div>
                    </template>
                    <template #head(promisedDate)>
                        <template v-if="promisedDateInline">Promised</template>
                        <i v-else class="fa fa-clock" title="Promised" v-b-tooltip.hover />
                    </template>
                    <template #cell(edit)="data">
                        <div class="table-icon-actions">
                            <router-link :to="getPrescriptionDetailsRoute(data.item)" v-if="showAction('edit')"
                                class="mr-1">
                                <b-icon icon="pencil-square" v-b-tooltip.hover title="Edit" />
                            </router-link>
                            <a href="#" @click.prevent="openSubscriptionForm(data.item)"
                                v-if="showSubscribeButton(data.item)">
                                <i class="fa fa-credit-card" aria-hidden="true" v-b-tooltip.hover
                                    title="Subscribe to Auto-Refill" />
                            </a>
                            <a href="#" @click.prevent="openUnsuppressForm(data.item)"
                                v-if="showSuppressButton(data.item)">
                                <i class="fa fa-phone-slash" aria-hidden="true" v-b-tooltip.hover
                                    :title="getSuppressionTitle(data.item)" />
                            </a>
                            <b-icon v-if="showAction('delete') && !disabled" icon="x-circle-fill" v-b-tooltip.hover
                                title="Remove" variant="danger" style="cursor: pointer"
                                @click="remove(data.item)"></b-icon>
                            <router-link v-if="showAction('verify')" :to="getRouteWithRxArgument('PV2', data.item)">
                                <i class="fas fa-clipboard-check" v-b-tooltip.hover :title="tooltipText(data.item)"
                                    :class="getVerifyActionButtonsClass(data.item)" style="cursor: pointer"></i>
                            </router-link>
                            <router-link v-if="showAction('pv1')" :to="getRouteWithRxArgument('PV1', data.item)">
                                <i class="fas fa-clipboard-check" v-b-tooltip.hover :title="tooltipText(data.item)"
                                    :class="getVerifyActionButtonsClass(data.item)" style="cursor: pointer"></i>
                            </router-link>
                            <b-button v-if="showAction('process-autorefill') && !data.item.needsReview" variant="success"
                                @click="openBatch([data.item])" :disabled="disabled || patient?.deceased" size="sm">
                                Process
                            </b-button>
                        </div>
                    </template>
                    <template v-if="showRowDetails" #row-details="row">
                        <div class="px-4">
                            <div>
                                Directions: {{ row.item.directions }}
                            </div>
                        </div>
                    </template>
                    <template #cell(locked)="data">
                        {{ data.item.lockedBy }}
                        <div v-if="data.item.lockExpires">
                            Lock Expires {{ formatLockExpires(data.item.lockExpires) }}
                        </div>
                    </template>
                </b-table>
                <div ref="mirrorScrollbar" class="mirror-scrollbar" @scroll="onMirrorScroll">
                    <div ref="mirrorScrollbarInner" class="mirror-scrollbar-inner"></div>
                </div>
                <BottomPagerBar
                    v-if="currentPerPage > 0 && (!hidePagerWhenNotNeeded || prescriptionsToShow.length > currentPerPage)"
                    v-model="pageNumber"
                    :total-rows="totalUnfilteredRows ?? (isTextFilterActive ? prescriptionsBeforeTextFilter.length : prescriptionsToShow.length)"
                    :filtered-rows="(totalUnfilteredRows != null || isTextFilterActive) ? prescriptionsToShow.length : undefined"
                    :per-page="currentPerPage"
                    :selected-rows="prescriptionsSelected.length"
                    show-total
                    show-page-size-selector
                    item-name="prescription"
                    :loading="isLoading"
                    scroll-target="#prescription-history-table"
                    @change-page-size="onPageSizeChange($event)"
                    @clear-selection="prescriptionsSelected = []"
                >
                    <template #left-after>
                        <slot name="pager-left-after"></slot>
                    </template>
                    <template #fab>
                        <slot name="pager-fab"></slot>
                    </template>
                </BottomPagerBar>
            </b-col>
        </b-row>

        <!-- PROBLEM CASES MODAL -->
        <CreateCaseModal
            modal-id="report-problem"
            :patient="patient"
            :preselected-prescriptions="prescriptionsSelected"
            @saved="onCaseSaved" />

        <!-- REFILLS BATCH MODAL-->
        <b-modal id="rx-modal" size="xl" :title="batchModalTitle" hide-footer no-close-on-backdrop
            @hide="onRxModalHide">
            <RxPage v-if="rxModalVisible" :refillBatchMode="refillBatchMode" :prescriptionsInBatch="rxForBatch"
                @batchFinished="batchFinished" :card="false">
            </RxPage>

            <hr>

            <b-button variant="dark" @click="batchFinished()">
                Cancel
            </b-button>
        </b-modal>
        <b-modal id="rx-unsuppress" title="Resume Reminders?" hide-footer>
            <p>Are you sure you want to resume reminders on this prescription?</p>
            <b-button @click="confirmUnsuppress" variant="success">Yes</b-button>
        </b-modal>
        <b-modal id="rx-suppress" title="Pause Reminders?" hide-footer>
            <v-select v-model="suppressReason" :options="suppressReasons">
            </v-select>
            <p>Are you sure you want to pause reminders on these prescriptions?</p>
            <b-button @click="confirmSuppress" variant="success">Yes</b-button>
        </b-modal>
        <!-- RX SUBSCRIPTION MODAL-->
        <b-modal id="rx-subscription" size="xl" title="Subscription to Auto-Refill" hide-footer>
            <RXSubscriptionForm :rx="rxToSubscribe" @saved="closeSubscriptionModal"></RXSubscriptionForm>
        </b-modal>
        <b-modal id="rx-transfer-modal" hide-footer title="Rx Transfer" size="lg" @shown="showRxTransfer">
            <RxTransferModalComponent id="rxTransferComponent" ref="rxTransferComponent" :patient="patient"
                @complete="$bvModal.hide('rx-transfer-modal')" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import axios from 'axios';
import moment from "moment";
import { BvModalEvent } from 'bootstrap-vue';
import { Prescription, RefillStatusCode } from '@/models/Prescription';
import { OrderOTCItem } from '@/models/Order';
import { RxTransferContents } from '@/models/RxTransfer';
import { Patient } from '@/models/Patient';
import SearchComponent from '@/components/SearchComponent.vue';
import RXSubscriptionForm from '@/components/Prescription/RXSubscriptionForm.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import RxTransferModalComponent from "@/components/RxTransfer/RxTransferModalComponent.vue";
import PromisedTimeSummary from "@/components/Order/PromisedTimeSummaryComponent.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import CreateCaseModal from "@/components/Cases/CreateCaseModal.vue";
import PhoneFormatter from '@/util/PhoneFormatter';
import PhoneCopyButton from '@/components/PhoneCopyButton.vue';

@Component({
    name: "PrescriptionsListComponent",
    components: {
        RxTransferModalComponent,
        //Importing this way prevents component circular reference
        RxPage: () => import('@/pages/Prescription/PrescriptionDetailsPage.vue'),
        SearchComponent, Checkbox,
        RXSubscriptionForm,
        PromisedTimeSummary,
        BottomPagerBar,
        CreateCaseModal,
        PhoneCopyButton,
    }
})
export default class PrescriptionsListComponent extends Vue {
    @Prop({ default: null }) private prescriptions!: Array<Prescription> | null;
    @Prop({ default: null }) private selected!: Array<Prescription> | null;
    @Prop({ default: false, type: Boolean }) protected hideActionsColumn!: boolean;
    @Prop({ default: false, type: Boolean }) private colorCodePrescriptions!: boolean;
    @Prop({ default: false, type: Boolean }) protected disabled!: boolean;
    @Prop({ default: false, type: Boolean }) protected showReportProblemButton!: false;
    @Prop({ default: false, type: Boolean }) protected showProccessQueueButton!: false;
    @Prop({ default: false, type: Boolean }) private showPatientName!: boolean;
    @Prop({ default: false, type: Boolean }) private showAddOns!: boolean;
    @Prop({ default: false, type: Boolean }) protected isLoading!: boolean;
    @Prop({ default: false, type: Boolean }) protected showTransferButton!: boolean;
    @Prop({ default: false, type: Boolean }) private alwaysShowVoid!: boolean;
    @Prop({ default: true, type: Boolean }) protected showSelectCheckox!: boolean;
    @Prop({ default: false, type: Boolean }) protected singleSelect!: boolean;
    @Prop({ default: true, type: Boolean }) protected showVoidBox!: boolean;
    @Prop({ default: false, type: Boolean }) private showFilter!: boolean;
    @Prop({ default: null, type: (Function || null) }) protected customSort!: Function | null;
    @Prop({ default: 0, type: Number }) protected perPage!: number;  // 0 = no pagination
    @Prop({ default: true, type: Boolean }) protected hidePagerWhenNotNeeded!: boolean;
    @Prop({ default: null, type: Number }) protected totalUnfilteredRows!: number | null;  // For showing filtered count in pager

    @Prop({ default: "New Refills In Batch", type: String }) protected batchModalTitle!: string;
    @Prop({ default: false, type: Boolean }) private refillBatchMode!: boolean;
    @Prop() protected processingButtonCallback!: Function;
    @Prop() private colorCodes?: Function;
    @Prop() private patientCountFn?: (patientID: number) => { visible: number; hidden: number } | null;

    @Prop() private orderID!: number;
    @Prop() private actionButtons!: Array<string>;
    @Prop() private mode!: string;
    @Prop() private tableEmptyText!: string;
    @Prop() private additionalFields!: Array<any>;
    @Prop() private fieldsKeysToRemove!: Array<any>;
    @Prop() private fieldsToRename!: Array<any>;
    @Prop() private defaultSort!: string;
    @Prop() private defaultDesc!: boolean;
    @Prop() protected patient!: Patient;

    @Prop() private manualSort?: Array<string>;
    @Prop({ default: false }) private rxIdLink!: boolean;
    @Prop({ default: false }) private showRowDetails!: boolean;
    @Prop({ default: false, type: Boolean }) private constrainColumnWidths!: boolean;
    @Prop({ default: false, type: Boolean }) private promisedDateInline!: boolean;

    @Ref() private rxTransferComponent!: RxTransferModalComponent;
    protected renderedItems: any[] = [];

    @Prop({ type: String, default: null }) protected currentSort!: string | null;
    @Prop({ type: Boolean, default: null }) protected currentDesc!: boolean | null;

    protected sortBy: string = "fillDate";
    protected sortDesc: boolean = true;

    protected rxToSubscribe: Prescription = new Prescription();
    private rxToUnsuppress: Prescription = new Prescription();

    moment = moment;

    private resizeObserver: ResizeObserver | null = null;
    private isSyncingScroll = false;

    mounted() {
        this.updateSort();
        this.currentPerPage = this.perPage;

        if (this.showRowDetails && this.renderedItems.length) {
            this.$set(this.renderedItems[0], '_showDetails', true);
        }

        this.initMirrorScrollbar();
    }

    beforeDestroy() {
        this.resizeObserver?.disconnect();
        this.getTableResponsiveEl()?.removeEventListener('scroll', this.onTableScroll);
    }

    private getTableResponsiveEl(): HTMLElement | null {
        const tableRef = this.$refs.prescriptionTable as any;
        const el = tableRef?.$el as HTMLElement | undefined;
        if (!el) return null;
        // BV generates class "table-responsive-true" when responsive="true" (string attr)
        if (el.classList.contains('table-responsive-true')) return el;
        return null;
    }

    private initMirrorScrollbar() {
        this.$nextTick(() => {
            const tableEl = this.getTableResponsiveEl();
            if (!tableEl) return;

            tableEl.addEventListener('scroll', this.onTableScroll);

            this.resizeObserver = new ResizeObserver(() => this.syncMirrorWidth());
            this.resizeObserver.observe(tableEl);
            // Also observe the inner table for column changes
            const innerTable = tableEl.querySelector('table');
            if (innerTable) this.resizeObserver.observe(innerTable);

            this.syncMirrorWidth();
        });
    }

    private syncMirrorWidth() {
        const tableEl = this.getTableResponsiveEl();
        const mirrorInner = this.$refs.mirrorScrollbarInner as HTMLElement;
        const mirrorOuter = this.$refs.mirrorScrollbar as HTMLElement;
        if (!tableEl || !mirrorInner || !mirrorOuter) return;

        const hasOverflow = tableEl.scrollWidth > tableEl.clientWidth;
        mirrorOuter.style.display = hasOverflow ? 'block' : 'none';
        mirrorInner.style.width = `${tableEl.scrollWidth}px`;
    }

    private onTableScroll() {
        if (this.isSyncingScroll) return;
        this.isSyncingScroll = true;
        const tableEl = this.getTableResponsiveEl();
        const mirror = this.$refs.mirrorScrollbar as HTMLElement;
        if (tableEl && mirror) mirror.scrollLeft = tableEl.scrollLeft;
        requestAnimationFrame(() => { this.isSyncingScroll = false; });
    }

    private onMirrorScroll() {
        if (this.isSyncingScroll) return;
        this.isSyncingScroll = true;
        const tableEl = this.getTableResponsiveEl();
        const mirror = this.$refs.mirrorScrollbar as HTMLElement;
        if (tableEl && mirror) tableEl.scrollLeft = mirror.scrollLeft;
        requestAnimationFrame(() => { this.isSyncingScroll = false; });
    }

    @Watch('perPage')
    onPerPagePropChange(val: number) {
        this.currentPerPage = val;
    }

    @Watch('sortBy')
    onSortByChange(val: string) {
        this.pageNumber = 1;
        this.$emit('update:currentSort', val);
    }

    @Watch('sortDesc')
    onSortDescChange(val: boolean) {
        this.pageNumber = 1;
        this.$emit('update:currentDesc', val);
    }

    @Watch('currentSort', { immediate: true })
    onCurrentSortPropChange(val: string | null) {
        if (val != null) {
            this.sortBy = val;
        }
    }

    @Watch('currentDesc', { immediate: true })
    onCurrentDescPropChange(val: boolean | null) {
        if (val != null) {
            this.sortDesc = val;
        }
    }

    @Watch('defaultSort')
    @Watch('defaultDesc')
    updateSort() {
        if (this.defaultSort) {
            this.sortBy = this.defaultSort;
        }

        // noinspection PointlessBooleanExpressionJS
        if (this.defaultDesc != null && this.defaultDesc != undefined) {
            this.sortDesc = this.defaultDesc;
        }
    }

    protected isSortable: boolean = true;
    private showVoidRxs: boolean = false;
    private filterText: string = '';
    protected rxForBatch: Array<Prescription> | null = [];
    private prescriptionsSelected: Prescription[] = [];
    private suppressReason = "";
    private hasOnlineOrderConflicts: boolean = false;
    private rxOnUnprocessedOrder: string = "";
    protected suppressReasons: string[] = ['No Longer Taking', 'Too Much On Hand', 'Too Expensive', 'Other'];

    // Pagination
    private pageNumber: number = 1;
    private currentPerPage: number = 0;

    // Range selection
    private lastSelectedIndex: number | null = null;

    /** Reset pagination to page 1 - can be called from parent via ref */
    public goToFirstPage() {
        this.pageNumber = 1;
    }

    /** Handle page size change from BottomPagerBar and emit to parent */
    private onPageSizeChange(newSize: number) {
        this.currentPerPage = newSize;
        this.$emit('change-page-size', newSize);
    }

    /** Get the index of an item in the current displayed list */
    private getItemIndex(item: Prescription): number {
        return this.paginatedPrescriptions.findIndex(p => p.rxID === item.rxID);
    }

    /** Select all items in a range, adding to existing selection */
    private selectRange(startIndex: number, endIndex: number) {
        const items = this.paginatedPrescriptions;
        const [from, to] = startIndex < endIndex
            ? [startIndex, endIndex]
            : [endIndex, startIndex];

        for (let i = from; i <= to; i++) {
            const item = items[i];
            if (item?.isActive && !this.prescriptionsSelected.some(p => p.rxID === item.rxID)) {
                this.prescriptionsSelected.push(item);
            }
        }
        this.lastSelectedIndex = endIndex;
    }

    get isManualSort() {
        return !!this.manualSort?.length;
    }

    get disableBatchRefill() {
        if (this.patient?.deceased) return true;
        if (this.prescriptionsSelected.length == 0) return true;
        if (!this.prescriptionsSelected.every(rx => !rx.isExpired && !rx.isVoid)) return true;
        return false;
    }

    get showOnlineOrderConflictAlert() {
        return this.hasOnlineOrderConflicts;
    }

    get disableTransfer() {
        if (this.prescriptionsSelected.length == 0) return true;
        if (!this.prescriptionsSelected.every(rx => !rx.isExpired && !rx.isVoid)) return true;
        return false;
    }

    get rxFields(): any[] {
        const addFieldKey = "_internal_add_field";

        let fields: any[] = [
            { key: "select", label: "", sortable: false, class: "col-compact", stickyColumn: true },
            { key: "hasAddons", label: "Adds", sortable: true, class: this.showAddOns ? "col-compact" : "d-none" },
            { key: "productNameShort", label: "Drug", sortable: this.isManualSort ? this.manualSort?.includes('productNameShort') : true, class: this.constrainColumnWidths ? "col-truncate" : "" },
            { key: "patientName", label: "Patient", sortable: this.isManualSort ? this.manualSort?.includes('patientName') : true, class: this.showPatientName ? (this.constrainColumnWidths ? "col-truncate" : "") : "d-none" },
            { key: "storeName", label: "Store", sortable: this.isManualSort ? this.manualSort?.includes('storeName') : false, class: this.constrainColumnWidths ? "col-truncate" : "" },
            { key: "rxId", label: "Rx Id", sortable: this.isManualSort ? this.manualSort?.includes('rxId') : false },
            { key: "daySupply", label: "DS", sortable: this.isManualSort ? this.manualSort?.includes('daySupply') : false, class: "col-compact text-center", tdClass: "text-center" },
            { key: "dispensed", label: "Dispensed", sortable: this.isManualSort ? this.manualSort?.includes('dispensed') : false, class: "col-compact text-center", tdClass: "text-center" },
            { key: "qtyLeft", label: "Qty Left", sortable: false, class: "text-center col-compact", },
            { key: "fillDate", label: "Date", sortable: this.isManualSort ? this.manualSort?.includes('fillDate') : true, sortDirection: "desc", formatter: this.dateFormatter, class: "col-narrow" },
            { key: "patientPay", label: "Pt Pay", sortable: this.isManualSort ? this.manualSort?.includes('patientPay') : true, class: "col-narrow" },
            { key: "programName", label: "Program", sortable: this.isManualSort ? this.manualSort?.includes('programName') : true, class: this.constrainColumnWidths ? "col-truncate" : "" },
            { key: "edit", label: "", class: "edit-col" },
        ];

        if (this.fieldsKeysToRemove?.length) {
            fields = fields.filter(col => !this.fieldsKeysToRemove.some(key => key == col.key));
        }

        if (this.fieldsToRename?.length) {
            this.fieldsToRename.forEach(ren => {
                let field = fields.find(col => col.key == ren.key);
                if (field) field.label = ren.label;
            });
        }

        if (this.additionalFields?.length) {
            const editCol = fields.find(col => col.key == 'edit');
            fields = fields.filter(col => col.key != 'edit');

            const addFields = this.additionalFields.map(af => {
                const addField = { ...af };
                addField.field[addFieldKey] = true;
                return addField;
            });

            const fieldsWithIndex = addFields.filter(col => col.index != null);
            if (fieldsWithIndex?.length) {
                fieldsWithIndex.forEach(col => {
                    fields.splice(col.index + 1, 0, col.field);
                });
            }

            const fieldsWithNoIndex = addFields.filter(col => col.index == null).map(col => col.field);
            if (fieldsWithNoIndex?.length) {
                fields = fields.concat(fieldsWithNoIndex);
            }

            fields.push(editCol);
        }

        return fields;
    }

    private incompleteAnswersFields: any[] = [
        { key: "patientName", label: "Patient", sortable: false },
        { key: "patientGender", label: "Gender", sortable: true, },
        { key: "patientPhone", label: "Phone", sortable: true, formatter: this.phoneFormatter },
        { key: "patientCell", label: "Cell", sortable: true, formatter: this.phoneFormatter },
        { key: "patientState", label: "State", sortable: true },
        { key: "rma", label: "RMA #", sortable: true },
        { key: "ipledge", label: "IPledge #" },
        { key: "dispenseBeforeDate", label: "Dispense before Date" },
        { key: "productNameShort", label: "Drug", sortable: true },
        { key: "storeName", label: "Store", sortable: false },
        // { key: "rxWorkflowStatus", label: "Rx Status", sortable: true },
        { key: "orderCreated", formatter: this.dateFormatter, sortable: true, },
        { key: "rxId", label: "Rx Id", sortable: false },
        {
            key: "fillDate",
            label: "Fill Date",
            sortable: true,
            sortDirection: "desc",
            formatter: this.dateFormatter
        },
        { key: "patientPay", label: "Patient Pay", sortable: true, },
        { key: "programName", label: "Program", sortable: true },
        { key: "edit", label: "", },
    ];

    get voidAndExpiredCount(): number {
        return this.prescriptions?.filter(rx => {
            const p = new Prescription(rx);
            return p.isVoid || p.isExpired;
        }).length ?? 0;
    }

    get filterTerms(): string[] {
        if (!this.filterText.trim()) return [];
        return this.filterText.toLowerCase().trim().split(/\s+/);
    }

    get internalFilteredPrescriptions(): Prescription[] {
        if (this.filterTerms.length === 0) {
            return this.prescriptions ?? [];
        }
        return (this.prescriptions ?? []).filter(rx => {
            const searchableFields = [
                rx.drugName,
                rx.productNameShort,
                rx.storeName,
                rx.rxNumber?.toString(),
                rx.rxID,
                rx.programName
            ].filter(Boolean).map(f => f!.toLowerCase());

            return this.filterTerms.every(term =>
                searchableFields.some(field => field.includes(term))
            );
        });
    }

    created() {
        this.isSortable = this.mode != 'print';
    }


    get selectedWithNoRemainingQuantity(): boolean {
        for (let i = 0; i < this.prescriptionsSelected.length; i++) {

            let rx = this.prescriptionsSelected[i];
            console.log("rx remainig: ", rx.rxID, rx.totalAllowedToDispense - rx.totalDispensed);
            if (rx.totalAllowedToDispense - rx.totalDispensed <= 0) {
                return true;
            }
        }
        return false;
    }

    DetermineFieldsByMode(mode: string) {
        if (mode == "IncompleteAnswers") {
            return this.incompleteAnswersFields;
        } else {
            return this.rxFields;
        }
    }

    get _tableEmptyText(): string {
        return this.tableEmptyText || "No Prescriptions to Show";
    }

    get actionSpecified() {
        return this.showAction('verify') || this.showAction('edit') || this.showAction('delete');
    }

    @Watch("sortDesc", { immediate: true })
    sortDirChange(_val: any, _oldVal: any) {
    }

    @Watch('prescriptions')
    changePrescriptions() {
        this.rxForBatch = [];
        if (this.prescriptionsSelected.length > 0) {
            const newRxIds = new Set(this.prescriptions?.map(p => p.rxID) ?? []);
            this.prescriptionsSelected = this.prescriptionsSelected.filter(p => newRxIds.has(p.rxID));
        }
        this.lastSelectedIndex = null;
    }

    /** Prescriptions after void/expired filter but before text filter - used for "total" count in pager */
    get prescriptionsBeforeTextFilter(): Array<Prescription> {
        return (this.prescriptions ?? []).map(rx => new Prescription(rx))
            .filter(rx => this.alwaysShowVoid || this.showVoidRxs || (!rx.isVoid && !rx.isExpired));
    }

    get prescriptionsToShow(): Array<Prescription> {
        return this.internalFilteredPrescriptions.map(rx => new Prescription(rx))
            .filter(rx => this.alwaysShowVoid || this.showVoidRxs || (!rx.isVoid && !rx.isExpired));
    }

    /** Whether the internal text filter is currently active */
    get isTextFilterActive(): boolean {
        return this.filterTerms.length > 0;
    }

    get sortedPrescriptions(): Array<Prescription> {
        const items = [...this.prescriptionsToShow];
        if (!this.sortBy || this.isManualSort) {
            return items;
        }

        // Use custom sort if provided, otherwise use default string comparison
        const sortFn = this.customSort || ((a: Prescription, b: Prescription, sortBy?: string, sortDesc?: boolean) => {
            const aVal = (a as any)[sortBy!];
            const bVal = (b as any)[sortBy!];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            let result: number;
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                result = aVal - bVal;
            } else {
                result = String(aVal).localeCompare(String(bVal));
            }
            return sortDesc ? -result : result;
        });

        items.sort((a, b) => sortFn(a, b, this.sortBy, this.sortDesc));
        return items;
    }

    public getPageItems(): Array<Prescription> {
        return this.paginatedPrescriptions;
    }

    get paginatedPrescriptions(): Array<Prescription> {
        if (!this.currentPerPage || this.currentPerPage <= 0) {
            return this.sortedPrescriptions;
        }
        const start = (this.pageNumber - 1) * this.currentPerPage;
        return this.sortedPrescriptions.slice(start, start + this.currentPerPage);
    }

    dateFormatter(value: Date, _key: string, _item: any) {
        const dt = moment.utc(value);
        if (dt.isValid()) return dt.format("L");
        return "";
    }

    phoneFormatter(value: string, _key: string, _item: any) {
        return PhoneFormatter.formatForDisplay(value);
    }

    showAction(action: string) {
        return this.actionButtons?.some(a => a == action);
    }

    remove(rxRow: Prescription) {
        this.$emit('prescriptionRemoved', rxRow);
    }

    getPrescriptionDetailsRoute(rxRow: Prescription) {
        return {
            name: 'PrescriptionDetails'
            , params: {
                storeID: rxRow.storeID,
                rxNumber: rxRow.rxNumber,
                rfNumber: (rxRow.rfNumber || 0)
            }
        };
    }

    getRouteWithRxArgument(routeName: string, rxRow: Prescription) {
        return {
            name: routeName
            , params: {
                orderID: this.orderID,
                storeID: rxRow.storeID,
                rxNumber: rxRow.rxNumber,
                rfNumber: (rxRow.rfNumber || 0)
            }
        };
    }

    getVerifyActionButtonsClass(rxRow: Prescription) {
        // If there's a dispense error, use color based on status
        if ((rxRow.dispenseErrorId || 0) > 0) {
            const status = rxRow.dispenseErrorStatus;
            return {
                'text-success': status === 'Resolved',           // Green - Resolved (pending acceptance)
                'text-danger': status === 'AwaitingResponse',    // Red - Awaiting response
                'text-info': status === 'Accepted',              // Blue - Accepted
                'text-warning': status === 'Created' || !status  // Yellow/Orange - Created or unknown
            };
        }

        // No dispense error - use verification status colors
        return {
            'text-info': (rxRow.pV2ID || 0) == 0,    // Blue - Not verified
            'text-success': (rxRow.pV2ID || 0) > 0   // Green - Verified (PV2)
        };
    }

    colorCodeTable(rx: Prescription, type: any) {
        if (this.colorCodes) {
            return this.colorCodes(rx, type);
        }
        if (!rx || type !== 'row' || !this.colorCodePrescriptions)
            return;
        if (rx.status == RefillStatusCode.TransferOut)
            return 'table-info';
        else if (rx.status == RefillStatusCode.Void)
            return 'table-danger strikethrough';
        else if (rx.status == RefillStatusCode.Discontinued)
            return 'table-danger font-italic';
        else if (rx.isExpired) return 'table-danger';
        else if (moment(rx.fillDate).isAfter(moment(), 'day'))
            return 'table-warning';
        else if (rx.status == RefillStatusCode.NonRefillable)
            return 'non-refillable-row';
        else if (!rx.isRefillableStatus)
            return 'table-dark';
    }

    isFuture(date: any) {
        return moment(date).isAfter(moment(), 'day');
    }

    rxIdTooltip(rx: Prescription): string {
        const lines = [`Memo: ${rx.memo ?? ''}`];
        if (!rx.isRefillableStatus) {
            const sub = rx.subStatusName ? ` (${rx.subStatusName})` : '';
            lines.push(`Status: ${rx.statusName}${sub}`);
        }
        if (rx.lastShippedDate) {
            const date = moment.utc(rx.lastShippedDate).format('L');
            const suffix = rx.lastShippedIsEquivalent ? ' (EQV)' : '';
            lines.push(`Last shipped: ${date}${suffix}`);
        }
        return lines.join('\n');
    }

    tooltipText(rxRow: Prescription) {
        let text: string;
        if ((rxRow.dispenseErrorId || 0) > 0) {
            const status = rxRow.dispenseErrorStatus;
            if (status === 'Resolved')
                text = 'Error resolved - pending acceptance';
            else if (status === 'AwaitingResponse')
                text = 'Error - awaiting response';
            else if (status === 'Accepted')
                text = 'Error accepted';
            else
                text = 'Error reported';
        } else if ((rxRow.pV2ID || 0) > 0) {
            text = 'Verified';
        } else {
            text = 'Verify';
        }

        return text;
    }

    onCaseSaved(caseId: number) {
        // Case saved successfully - modal closes itself
    }

    @Watch('selected')
    buttonSelect() {
        this.prescriptionsSelected = this.selected ?? [];
    }

    @Watch('prescriptionsSelected')
    inputSelectedRxs(newSelectedRxs: Array<Prescription>) {
        this.$emit('selection-changed', newSelectedRxs);
        this.checkForOnlineOrderConflicts();
    }

    isSamePrescriptionOrder(rx1: Prescription, rx2: Prescription) {
        return rx1.storeID == rx2.storeID && rx1.rxNumber == rx2.rxNumber &&
            rx1.rfNumber == rx2.rfNumber && rx1.orderId == rx2.orderId;
    }

    async checkForOnlineOrderConflicts() {
        try {
            if (this.prescriptionsSelected.length === 0 || !this.patient?.id) {
                this.hasOnlineOrderConflicts = false;
                return;
            }

            const response = await axios.get(`/PatientWeb/GetUnprocessed?patientId=${this.patient.id}`);
            const unprocessedOrders = response.data;

            const conflictingPrescriptions = this.findPrescriptionsInUnprocessedOrders(this.prescriptionsSelected, unprocessedOrders);

            this.hasOnlineOrderConflicts = conflictingPrescriptions.length > 0;
            this.rxOnUnprocessedOrder = conflictingPrescriptions.map(rx => rx.rxNumber).join(', ');
        } catch (error) {
            console.error('Error checking online orders for alert:', error);
            this.hasOnlineOrderConflicts = false;
        }
    }

    //Take only the max RfNumber from group of StoreId - RxNbr
    async openBatch(rxForBatch: Array<Prescription>) {
        // Check if the patient is deceased
        if (this.patient?.deceased) {
            this.$notification(NotificationOptions.error('Cannot create refills for deceased patients.'));
            return;
        }

        if (this.selectedWithNoRemainingQuantity) {
            // Show your alert here.
            this.$notification(
                NotificationOptions.notificationOptionsPreset(
                    'Cannot create a new batch refill, selected prescriptions have no refills available.',
                    NotificationOptions.NotificationTypes.warning));
            return;
        }

        // Check if any selected prescriptions are in unprocessed online orders
        if (this.patient?.id) {
            try {
                const response = await axios.get(`/PatientWeb/GetUnprocessed?patientId=${this.patient.id}`);
                const unprocessedOrders = response.data;

                // Get the prescriptions we're trying to batch
                const prescriptionsToCheck = this.prescriptionsSelected?.length ? this.prescriptionsSelected : (rxForBatch?.length ? rxForBatch : this.prescriptions);

                if (prescriptionsToCheck && prescriptionsToCheck.length > 0) {
                    const conflictingPrescriptions = this.findPrescriptionsInUnprocessedOrders(prescriptionsToCheck, unprocessedOrders);

                    if (conflictingPrescriptions.length > 0) {
                        this.hasOnlineOrderConflicts = true;
                        this.rxOnUnprocessedOrder = conflictingPrescriptions.map(rx => rx.rxNumber).join(', ');
                        return;
                    } else {
                        this.hasOnlineOrderConflicts = false;
                    }
                }
            } catch (error) {
                console.error('Error checking online orders:', error);
                // Continue with batch processing if the check fails - don't block users
            }
        }

        if (this.refillBatchMode) {
            if (this.prescriptionsSelected?.length) {
                const sameStoreRx = (rx: any, rx2: any) => {
                    return rx.storeID == rx2.storeID && rx.rxNumber == rx2.rxNumber;
                };

                const findMaxRfInStoreRxGroup = (rx1: Prescription, rx2: Prescription): Prescription => {
                    return (rx1.rfNumber || 0) > (rx2.rfNumber ?? 0) ? rx1 : rx2;
                };

                //Find the distinct StoreId - RxNumber combinations in the selection
                const distinctStoreRxList = this.prescriptionsSelected.filter((rx, i, rxs) => {
                    const i2 = rxs.findIndex(rx2 => sameStoreRx(rx, rx2));
                    return i2 == i;
                }).map(rx => {
                    return { storeID: rx.storeID, rxNumber: rx.rxNumber };
                });

                //Find the prescription with the max RfNumber for each distinct StoreId - RxNumber combination
                this.rxForBatch = distinctStoreRxList.map(ids => {
                    const group = this.prescriptionsToShow.filter(rx => sameStoreRx(rx, ids));
                    return Object.assign(new Prescription(), group.reduce(findMaxRfInStoreRxGroup));
                });
            } else {
                this.rxForBatch = rxForBatch;
            }
        } else {
            if (this.prescriptionsSelected?.length)
                this.rxForBatch = this.prescriptionsSelected;
            else if (rxForBatch?.length)
                this.rxForBatch = rxForBatch;
            else
                this.rxForBatch = this.prescriptions;
        }

        this.rxModalVisible = true;
        this.$bvModal.show('rx-modal');
    }

    // Gates the embedded RxPage (PrescriptionDetailsPage). Destroying it at hide-start runs its
    // LockHandler teardown, releasing any prescription locks immediately — a reopen that
    // interrupts the close transition would otherwise reuse the instance and its stale locks.
    private rxModalVisible = false;

    onRxModalHide() {
        this.rxModalVisible = false;
    }

    batchFinished(rx?: Prescription) {
        this.$bvModal.hide('rx-modal');
        this.$emit('batchFinished', rx);
    }

    openSubscriptionForm(rx: Prescription) {
        this.rxToSubscribe = rx;
        this.$bvModal.show('rx-subscription');
    }

    openUnsuppressForm(rx: Prescription) {
        this.rxToUnsuppress = rx;
        this.$bvModal.show('rx-unsuppress');
    }

    closeSubscriptionModal() {
        this.$bvModal.hide('rx-subscription');
        this.$emit('newSubscriptionSaved');
    }

    showSubscribeButton(rx: Prescription) {
        return this.showAction('subscribe') && rx.isAvailableForAutorefill;
    }

    showSuppressButton(rx: Prescription) {
        return this.showAction('suppress') && rx.suppression != null;
    }

    getSuppressionTitle(rx: Prescription) {
        return `${rx.suppression.reason} on ${new Date(rx.suppression.created).toLocaleDateString(navigator.language, {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric'
        })}`;
    }

    confirmUnsuppress() {
        this.$bvModal.hide('rx-unsuppress');
        var supp = this.rxToUnsuppress.suppression;

        supp.active = false;

        axios.post("/Prescription/Suppression", supp).then((_response: any) => {
            this.$notification(
                NotificationOptions.successSaveNotificationPreset(
                    `Prescription reminders resumed for ${this.rxToUnsuppress.rxID}`
                )
            );
            this.$emit('batchFinished', this.rxToUnsuppress);
        }).catch(err => this.$notification(NotificationOptions.error(err)));
    }

    confirmSuppress() {
        this.$bvModal.hide('rx-suppress');
        var ctr = 0;
        this.prescriptionsSelected.forEach((rx: Prescription, _index: number, array: Prescription[]) => {
            var supp = {
                storeId: rx.storeID,
                rxNumber: rx.rxNumber,
                active: true,
                reason: this.suppressReason
            };
            axios.post("/Prescription/Suppression", supp).then((_response: any) => {
                this.$notification(
                    NotificationOptions.successSaveNotificationPreset(
                        `Prescription reminders paused for ${rx.rxID}`
                    )
                );
                ctr++;
                if (ctr == array.length) {
                    this.$emit('batchFinished', rx);
                }
            }).catch(err => this.$notification(NotificationOptions.error(err)));
        });
    }

    getAddOns(AddOnsList: OrderOTCItem) {
        if (AddOnsList != null) {
            let a = '<strong> Add Ons </strong>'
            Object.entries(AddOnsList).forEach(
                ([_key, value]) =>
                    a += '<br /> ' + value.otcItemName + ': ' + value.quantity + (value.quantity == 1 ? ' pc' : ' pcs')
            )
            return a;
        }
        return '';
    }

    showRxTransfer() {
        const contents = this.prescriptionsSelected.map(rx => {
            return { medicationName: rx.drugName, rxNumber: rx.rxNumber.toString() } as unknown as RxTransferContents
        });
        if (this.rxTransferComponent) this.rxTransferComponent.startingContents = contents;
    }

    transferOut() {
        return this.prescriptionsSelected;
    }

    selectRow(item: Prescription, _index: number, event: Event) {
        if (this.showRowDetails) {
            this.$set(item, "_showDetails", !(item as any)._showDetails);
        }

        if (!this.showSelectCheckox) return;
        if (!item.isActive) return;

        const mouseEvent = event as MouseEvent;
        const currentIndex = this.getItemIndex(item);

        // Handle shift+click for range selection (multi-select mode only)
        if (mouseEvent?.shiftKey && this.lastSelectedIndex !== null && !this.singleSelect) {
            this.selectRange(this.lastSelectedIndex, currentIndex);
            return;
        }

        const foundIdx = this.prescriptionsSelected.findIndex(value => value.rxID === item.rxID);
        if (this.singleSelect) {
            if (foundIdx === -1) {
                this.prescriptionsSelected = [item];
            } else {
                this.prescriptionsSelected = [];
            }
            this.lastSelectedIndex = currentIndex;
            return;
        }
        if (foundIdx === -1) {
            this.prescriptionsSelected.push(item);
        } else {
            this.prescriptionsSelected.splice(foundIdx, 1);
        }

        // Track last selected for future shift+click
        this.lastSelectedIndex = currentIndex;
    }

    /** Handle checkbox click with shift+click range selection support */
    handleCheckboxClick(event: MouseEvent, item: Prescription) {
        if (!item.isActive) return;

        const currentIndex = this.getItemIndex(item);

        // Handle shift+click for range selection (multi-select mode only)
        if (event.shiftKey && this.lastSelectedIndex !== null && !this.singleSelect) {
            this.selectRange(this.lastSelectedIndex, currentIndex);
            return;
        }

        const foundIdx = this.prescriptionsSelected.findIndex(p => p.rxID === item.rxID);
        if (this.singleSelect) {
            if (foundIdx === -1) {
                this.prescriptionsSelected = [item];
            } else {
                this.prescriptionsSelected = [];
            }
            this.lastSelectedIndex = currentIndex;
            return;
        }

        // Toggle selection
        if (foundIdx === -1) {
            this.prescriptionsSelected.push(item);
        } else {
            this.prescriptionsSelected.splice(foundIdx, 1);
        }

        this.lastSelectedIndex = currentIndex;
    }

    findPrescriptionsInUnprocessedOrders(prescriptions: Prescription[], unprocessedOrders: any[]): Prescription[] {
        return prescriptions.filter(rx =>
            unprocessedOrders.some(order =>
                order.prescriptions?.some((onlineRx: any) =>
                    onlineRx.storeId === rx.storeID && onlineRx.rxNumber === rx.rxNumber
                )
            )
        );
    }

    formatLockExpires(value: Date) {
        return moment.utc(value).fromNow();
    }
}

export class PrescriptionListColumn {
    public key?: string;
    public label?: string;
    public sortable?: boolean;
    public class?: string;
    public sortDirection?: string;
    public formatter?: Function;
}

</script>

<style scoped>
::v-deep(.tooltip-inner) {
    white-space: pre-wrap;
}

::v-deep(.strikethrough td) {
    text-decoration: line-through;
}

::v-deep(.edit-col) {
    width: 30px;
}

::v-deep(.b-table-sticky-column) {
    background-color: inherit;
}

/* Hide the native scrollbar on the table wrapper — the mirror scrollbar replaces it */
::v-deep([class*="table-responsive"]) {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
}
::v-deep([class*="table-responsive"]::-webkit-scrollbar) {
    display: none; /* Chrome/Safari */
}

.mirror-scrollbar {
    --pager-bar-height: 50px;
    overflow-x: auto;
    overflow-y: hidden;
    display: none;
    position: sticky;
    bottom: var(--pager-bar-height); /* sits above the sticky BottomPagerBar */
    z-index: 21; /* above pager bar z-index of 20 */
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.mirror-scrollbar-inner {
    height: 1px;
}
</style>
